import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { prettifyKey } from '@erp/shared-utils';
import { T, useGT } from 'gt-react';
import type { ConfigRendererProps } from './types';
import {
  type JsonSchema,
  buildConfigDefaults,
  defaultFromJsonSchema,
  getSchemaType,
  isRecord,
} from '@/lib/workflows/jsonSchemaDefaults';
import { useStepTypeConfigSchema } from '@/api/rpc/hooks/workflows/useWorkflowDefinitions';

function getTitle(schema: JsonSchema, fallback: string): string {
  return typeof schema.title === 'string' && schema.title.trim()
    ? schema.title
    : prettifyKey(fallback);
}

function getDescription(schema: JsonSchema): string | undefined {
  return typeof schema.description === 'string'
    ? schema.description
    : undefined;
}

function unionLabel(schema: JsonSchema, index: number): string {
  const title = schema.title;
  if (typeof title === 'string' && title.trim()) return title;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return String(schema.enum[0]);
  }
  if ('const' in schema) return String(schema.const);
  return `Option ${index + 1}`;
}

function getUnionSchemas(schema: JsonSchema): JsonSchema[] {
  const candidates = [schema.oneOf, schema.anyOf].find(Array.isArray);
  if (!Array.isArray(candidates)) return [];
  return candidates.filter(isRecord);
}

function schemaHasValue(schema: JsonSchema, value: unknown): boolean {
  if ('const' in schema) return Object.is(schema.const, value);
  if (Array.isArray(schema.enum))
    return schema.enum.some((item) => Object.is(item, value));
  return false;
}

function pickUnionIndex(schema: JsonSchema, value: unknown): number {
  const unionSchemas = getUnionSchemas(schema);
  if (unionSchemas.length === 0) return 0;
  const explicit = unionSchemas.findIndex((item) =>
    schemaHasValue(item, value),
  );
  if (explicit >= 0) return explicit;
  return 0;
}

function deriveUnionValue(schema: JsonSchema, value: unknown): unknown {
  const unionSchemas = getUnionSchemas(schema);
  if (unionSchemas.length === 0) return value;
  const active = unionSchemas[pickUnionIndex(schema, value)] ?? unionSchemas[0];
  return value ?? defaultFromJsonSchema(active);
}

function PrimitiveField({
  schema,
  value,
  onChange,
  readOnly,
  labelFallback,
}: {
  schema: JsonSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly: boolean;
  labelFallback: string;
}) {
  const gt = useGT();
  const schemaType = getSchemaType(schema);
  const label = getTitle(schema, labelFallback);

  if (schemaType === 'boolean') {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border p-3">
        <div className="space-y-1">
          <Label>{label}</Label>
          {getDescription(schema) ? (
            <p className="text-xs text-muted-foreground">
              {getDescription(schema)}
            </p>
          ) : null}
        </div>
        <Checkbox
          checked={Boolean(value)}
          disabled={readOnly}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
      </div>
    );
  }

  if (Array.isArray(schema.enum)) {
    const enumOptions = schema.enum;
    const selectedIndex = enumOptions.findIndex((option) =>
      Object.is(option, value),
    );
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <Select
          disabled={readOnly}
          value={selectedIndex >= 0 ? String(selectedIndex) : ''}
          onValueChange={(next) => onChange(enumOptions[Number(next)])}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={gt('Select value', {
                context: 'enum field placeholder in config renderer',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {enumOptions.map((option, index) => (
              <SelectItem key={index} value={String(index)}>
                {String(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (schemaType === 'number' || schemaType === 'integer') {
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        <Input
          type="number"
          value={typeof value === 'number' ? String(value) : ''}
          disabled={readOnly}
          onChange={(event) => {
            if (event.target.value === '') {
              onChange(undefined);
              return;
            }
            const parsed = Number(event.target.value);
            if (!Number.isNaN(parsed)) {
              onChange(parsed);
            }
          }}
        />
      </div>
    );
  }

  if (schemaType === 'string' || schemaType === undefined) {
    const multiline =
      schema.format === 'multiline' || schema.format === 'textarea';
    return (
      <div className="space-y-1.5">
        <Label>{label}</Label>
        {multiline ? (
          <Textarea
            value={typeof value === 'string' ? value : ''}
            disabled={readOnly}
            onChange={(event) => onChange(event.target.value)}
          />
        ) : (
          <Input
            value={typeof value === 'string' ? value : ''}
            disabled={readOnly}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
        {getDescription(schema) ? (
          <p className="text-xs text-muted-foreground">
            {getDescription(schema)}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Textarea
        value={
          typeof value === 'string'
            ? value
            : JSON.stringify(value ?? '', null, 2)
        }
        disabled={readOnly}
        onChange={(event) => {
          const text = event.target.value;
          try {
            onChange(JSON.parse(text));
          } catch {
            onChange(text);
          }
        }}
      />
    </div>
  );
}

function SchemaField({
  schema,
  value,
  onChange,
  path,
  readOnly,
  labelFallback,
}: {
  schema: JsonSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  path: string[];
  readOnly: boolean;
  labelFallback: string;
}) {
  const schemaType = getSchemaType(schema);
  const unionSchemas = getUnionSchemas(schema);

  if (unionSchemas.length > 0) {
    const activeIndex = pickUnionIndex(schema, value);
    const activeSchema = unionSchemas[activeIndex] ?? unionSchemas[0];
    return (
      <div className="space-y-3 rounded-md border p-3">
        <div className="space-y-1.5">
          <Label>{getTitle(schema, labelFallback)}</Label>
          {getDescription(schema) ? (
            <p className="text-xs text-muted-foreground">
              {getDescription(schema)}
            </p>
          ) : null}
        </div>
        {unionSchemas.length > 1 ? (
          <Select
            value={String(activeIndex)}
            disabled={readOnly}
            onValueChange={(next) => {
              const nextSchema = unionSchemas[Number(next)] ?? activeSchema;
              onChange(defaultFromJsonSchema(nextSchema));
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {unionSchemas.map((option, index) => (
                <SelectItem key={index} value={String(index)}>
                  {unionLabel(option, index)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <SchemaField
          schema={activeSchema}
          value={deriveUnionValue(schema, value)}
          onChange={onChange}
          path={path}
          readOnly={readOnly}
          labelFallback={labelFallback}
        />
      </div>
    );
  }

  if (schemaType === 'object' || isRecord(schema.properties)) {
    const properties = isRecord(schema.properties) ? schema.properties : {};
    const objectValue = isRecord(value) ? value : {};

    return (
      <div className="space-y-3 rounded-md border p-3">
        <div className="space-y-1.5">
          <Label>{getTitle(schema, labelFallback)}</Label>
          {getDescription(schema) ? (
            <p className="text-xs text-muted-foreground">
              {getDescription(schema)}
            </p>
          ) : null}
        </div>
        <div className="space-y-3">
          {Object.entries(properties).map(([key, propSchema]) => {
            if (!isRecord(propSchema)) return null;
            const nextPath = [...path, key];
            return (
              <SchemaField
                key={key}
                schema={propSchema}
                value={objectValue[key]}
                onChange={(nextValue) =>
                  onChange({
                    ...objectValue,
                    [key]: nextValue,
                  })
                }
                path={nextPath}
                readOnly={readOnly}
                labelFallback={getTitle(propSchema, key)}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (schemaType === 'array') {
    const itemsSchema = isRecord(schema.items) ? schema.items : undefined;
    const arrayValue = Array.isArray(value) ? value : [];

    return (
      <div className="space-y-3 rounded-md border p-3">
        <div className="space-y-1.5">
          <Label>{getTitle(schema, labelFallback)}</Label>
          {getDescription(schema) ? (
            <p className="text-xs text-muted-foreground">
              {getDescription(schema)}
            </p>
          ) : null}
        </div>
        <div className="space-y-3">
          {arrayValue.map((item, index) => (
            <div
              key={index}
              className="rounded-md border bg-muted/20 p-3 space-y-3"
            >
              {itemsSchema ? (
                <SchemaField
                  schema={itemsSchema}
                  value={item}
                  onChange={(nextValue) =>
                    onChange(
                      arrayValue.map((entry, itemIndex) =>
                        itemIndex === index ? nextValue : entry,
                      ),
                    )
                  }
                  path={[...path, String(index)]}
                  readOnly={readOnly}
                  labelFallback={`${labelFallback} ${index + 1}`}
                />
              ) : (
                <Textarea
                  value={
                    typeof item === 'string'
                      ? item
                      : JSON.stringify(item, null, 2)
                  }
                  disabled={readOnly}
                  onChange={(event) => {
                    const next = arrayValue.map((entry, itemIndex) =>
                      itemIndex === index ? event.target.value : entry,
                    );
                    onChange(next);
                  }}
                />
              )}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={readOnly}
                  onClick={() => {
                    onChange(
                      arrayValue.filter((_, itemIndex) => itemIndex !== index),
                    );
                  }}
                >
                  <MinusIcon className="mr-2 size-4" />
                  <T context="button to remove an array item in config renderer">
                    Remove
                  </T>
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={readOnly}
          onClick={() => {
            const nextItem = itemsSchema
              ? defaultFromJsonSchema(itemsSchema)
              : null;
            onChange([...arrayValue, nextItem]);
          }}
        >
          <PlusIcon className="mr-2 size-4" />
          <T context="button to add an item to an array field in config renderer">
            Add Item
          </T>
        </Button>
      </div>
    );
  }

  return (
    <PrimitiveField
      schema={schema}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      labelFallback={labelFallback}
    />
  );
}

export function StandardConfigRenderer({
  node,
  config,
  onConfigChange,
  readOnly = false,
  hiddenKeys = [],
}: ConfigRendererProps & {
  /** Config keys a wrapping renderer draws with its own control. */
  hiddenKeys?: readonly string[];
}) {
  // `listStepTypes` omits config schemas by default, so fall back to fetching
  // just this step type's schema. Callers that do request schemas up front
  // (the step tester) keep hitting the inline copy and never fetch.
  const inlineSchema = node.data._stepTypeData?.configSchema;
  const { data: fetchedSchema } = useStepTypeConfigSchema(
    inlineSchema ? undefined : node.data.stepType,
  );
  const schema = inlineSchema ?? fetchedSchema;

  if (!schema || !isRecord(schema)) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-sm text-muted-foreground">
        <T context="empty state when a workflow step has no configuration options">
          This step has no configuration options.
        </T>
      </div>
    );
  }

  // `batch` is rendered once by the panel's Advanced section for every step
  // (see BatchOptionsFields) — it is engine behaviour, not per-step config, and
  // the generic object form here would be both a duplicate and a worse control.
  const hidden = new Set(hiddenKeys);
  const properties = Object.fromEntries(
    Object.entries(isRecord(schema.properties) ? schema.properties : {}).filter(
      ([key]) => key !== 'batch' && !hidden.has(key),
    ),
  );
  if (Object.keys(properties).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-sm text-muted-foreground">
        <T context="empty state when a workflow step has no configuration options">
          This step has no configuration options.
        </T>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(properties).map(([key, propSchema]) => {
        if (!isRecord(propSchema)) return null;
        return (
          <SchemaField
            key={key}
            schema={propSchema}
            value={config[key]}
            onChange={(nextValue) => {
              if (readOnly) return;
              onConfigChange({
                ...config,
                [key]: nextValue,
              });
            }}
            path={[key]}
            readOnly={readOnly}
            labelFallback={getTitle(propSchema, key)}
          />
        );
      })}
      {!readOnly ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onConfigChange(buildConfigDefaults(schema))}
          >
            <T context="button to reset all config fields to their default values">
              Reset to defaults
            </T>
          </Button>
        </div>
      ) : null}
      <Separator />
      <div className="text-xs text-muted-foreground">
        <T context="footer note in workflow step config renderer">
          Values are derived from the step schema.
        </T>
      </div>
    </div>
  );
}
